// "Load more" for collection, search and blog grids. Without JavaScript the button is a normal link
// to the next page. With JavaScript the next page is fetched ahead of time (as soon as the button
// gets near the screen, or the pointer/finger reaches it), so a tap shows the products right away.
// The list then opens smoothly, so the button visibly travels down and the box grows with it.
class SLoadMore extends HTMLElement {
  #prefetched = null; // { href, promise }

  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.link = this.querySelector('a[data-load-more]');
    if (!this.link) return;
    this.link.addEventListener('click', (event) => this.#load(event), { signal });
    this.link.addEventListener('pointerenter', () => this.#prefetch(), { signal });
    this.link.addEventListener('pointerdown', () => this.#prefetch(), { signal });
    this.link.addEventListener('focus', () => this.#prefetch(), { signal });
    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) this.#prefetch();
    }, { rootMargin: '1200px 0px' });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
  }

  #prefetch() {
    const href = this.link?.href;
    if (!href || this.#prefetched?.href === href) return this.#prefetched?.promise;
    const promise = fetch(href, { signal: this.controller.signal, priority: 'high' })
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        // Warm up the first images so they appear with the products.
        doc.querySelectorAll(`#${CSS.escape(this.getAttribute('grid'))} > li img`).forEach((img, i) => {
          if (i > 7) return;
          const pre = new Image();
          if (img.getAttribute('srcset')) { pre.sizes = img.getAttribute('sizes') || '50vw'; pre.srcset = img.getAttribute('srcset'); }
          else if (img.getAttribute('src')) pre.src = img.getAttribute('src');
        });
        return doc;
      });
    promise.catch(() => { if (this.#prefetched?.promise === promise) this.#prefetched = null; });
    this.#prefetched = { href, promise };
    return promise;
  }

  async #load(event) {
    event.preventDefault();
    if (this.hasAttribute('aria-busy')) return;
    const grid = document.getElementById(this.getAttribute('grid'));
    if (!grid || !this.link) return;
    this.setAttribute('aria-busy', 'true');
    try {
      const doc = await this.#prefetch();
      const items = doc.querySelectorAll(`#${CSS.escape(grid.id)} > li`);
      const fromH = grid.getBoundingClientRect().height;
      items.forEach((item) => grid.append(document.importNode(item, true)));
      const nextLink = doc.querySelector(`s-load-more[grid="${grid.id}"] a[data-load-more]`);
      if (nextLink) {
        this.link.href = nextLink.href;
      } else {
        this.remove();
      }
      if (!matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const toH = grid.getBoundingClientRect().height;
        if (toH > fromH + 1) {
          grid.classList.add('xo-growing');
          grid.animate([{ height: fromH + 'px' }, { height: toH + 'px' }], { duration: 650, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' })
            .finished.catch(() => {}).then(() => grid.classList.remove('xo-growing'));
        }
      }
      const status = this.querySelector('[data-load-more-status]') || document.querySelector('[data-load-more-status]');
      if (status) status.textContent = this.dataset.loadedMessage || '';
      if (items.length) grid.querySelector(`li:nth-child(${grid.children.length - items.length + 1}) a`)?.focus({ preventScroll: true });
      // Get the following page ready too.
      if (nextLink && this.isConnected) this.#prefetch();
    } catch (error) {
      if (error.name !== 'AbortError') window.location.href = this.link.href;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('s-load-more')) customElements.define('s-load-more', SLoadMore);
